// Ordinary buildings: lot subdivision, facades, roofs, rooftop props, awnings, parks.
import * as THREE from 'three';
import { facadeBox, facadeSoup, aabb, mat, QuadSoup, type BuildCtx } from './city_common';
import type { FacadeStyle } from './city_textures';
import type { Block } from './city_layout';
import { pick, randRange } from './util';

const CORNICE: Record<string, number> = {
  classic: 0xefe4cc,
  terracotta: 0xe6c6ac,
  modern: 0x2a3038,
  brick: 0xd6cbb8,
  panel: 0xb4b4ac,
  gothic: 0xf0e8d8,
  white: 0xdcdcd8,
  stone: 0xe8e0cc,
};

export interface BuildingOpts {
  style: FacadeStyle;
  shops: boolean;
  floors: number;
  tint?: THREE.Color;
  /** skip rooftop props */
  plain?: boolean;
  /** which sides face a street (for awnings / signs): 'N','S','E','W' */
  streetSides?: string;
  collider?: boolean;
}

const _tint = new THREE.Color();

/** One textured box building. Returns its top height. */
export function addBuildingBox(ctx: BuildCtx, x0: number, z0: number, x1: number, z1: number, o: BuildingOpts): number {
  const st = o.style;
  const rng = ctx.rng;
  const tint = o.tint ?? _tint.set(pick(rng, st.tints)).clone();
  const groundH = o.shops ? ctx.tex.shop.tileH : 0;
  const h = groundH + o.floors * st.floorH;
  const uOff = Math.floor(rng() * st.bays) * st.bayW;
  const vOff = Math.floor(rng() * st.floors) * st.floorH;
  const soup = facadeSoup(ctx, st.key);
  if (o.shops) {
    const sh = ctx.tex.shop;
    const pad = 0.15;
    const su = Math.floor(rng() * sh.bays) * sh.bayW;
    facadeBox(facadeSoup(ctx, 'shop'), sh, x0 - pad, z0 - pad, x1 + pad, z1 + pad, 0, groundH, su, 0, _tint.set(0xffffff));
    // plinth cap
    ctx.solid.box((x0 + x1) / 2, groundH - 0.05, (z0 + z1) / 2, x1 - x0 + pad * 2 + 0.3, 0.3, z1 - z0 + pad * 2 + 0.3, CORNICE[st.key] ?? 0xe0d8c8);
    // awnings + hanging signs on street sides
    const sides = o.streetSides ?? '';
    const faces: [number, number, number, number, number, number][] = [];
    if (sides.includes('S')) faces.push([x0, z1 + pad, x1, z1 + pad, 0, 1]);
    if (sides.includes('N')) faces.push([x0, z0 - pad, x1, z0 - pad, 0, -1]);
    if (sides.includes('E')) faces.push([x1 + pad, z0, x1 + pad, z1, 1, 0]);
    if (sides.includes('W')) faces.push([x0 - pad, z0, x0 - pad, z1, -1, 0]);
    const AWN = [0x8b1e2d, 0x2d5a27, 0x1e3a5f, 0xb8860b, 0x5b2a86, 0x333333];
    for (const f of faces) {
      const len = Math.hypot(f[2] - f[0], f[3] - f[1]);
      const n = Math.floor(len / 5);
      for (let i = 0; i < n; i++) {
        const t = (i + 0.5) / n;
        const px = f[0] + (f[2] - f[0]) * t, pz = f[1] + (f[3] - f[1]) * t;
        const ry = Math.atan2(f[4], f[5]);
        if (rng() < 0.4) {
          const a = 0.38;
          ctx.solid.add(new THREE.BoxGeometry(3.2, 0.08, 1.3), mat(px + f[4] * 0.65 * Math.cos(a), 3.1 - 0.65 * Math.sin(a), pz + f[5] * 0.65 * Math.cos(a), ry, 1, 1, 1, a), pick(rng, AWN));
        } else if (rng() < 0.3) {
          // perpendicular hanging sign
          const sx = px + f[4] * 0.5 - f[5] * 1.6, sz = pz + f[5] * 0.5 + f[4] * 1.6;
          ctx.solid.add(new THREE.BoxGeometry(0.08, 0.7, 0.9), mat(sx, 3.4, sz, ry), pick(rng, AWN));
          ctx.metal.add(new THREE.BoxGeometry(0.04, 0.04, 0.9), mat(px + f[4] * 0.5, 3.8, pz + f[5] * 0.5, ry), 0x333333);
        }
      }
    }
  }
  facadeBox(soup, st, x0, z0, x1, z1, groundH, h, uOff, vOff, tint);
  // roof + cornice
  ctx.roofs.floor(x0, z0, x1, z1, h, 12);
  const cc = CORNICE[st.key] ?? 0xe0d8c8;
  if (st.key === 'modern') {
    ctx.solid.box((x0 + x1) / 2, h, (z0 + z1) / 2, x1 - x0 + 0.2, 0.9, z1 - z0 + 0.2, cc);
  } else {
    ctx.solid.box((x0 + x1) / 2, h - 0.5, (z0 + z1) / 2, x1 - x0 + 0.8, 0.5, z1 - z0 + 0.8, cc);
    ctx.solid.box((x0 + x1) / 2, h, (z0 + z1) / 2, x1 - x0 + 0.3, 0.6, z1 - z0 + 0.3, cc);
  }
  if (!o.plain) addRoofProps(ctx, x0, z0, x1, z1, h + (st.key === 'modern' ? 0 : 0.6), st.key);
  if (o.collider !== false) {
    ctx.colliders.push(aabb(x0, z0, x1, z1, h));
    ctx.buildingRects.push(aabb(x0, z0, x1, z1, h));
  }
  return h;
}

function addRoofProps(ctx: BuildCtx, x0: number, z0: number, x1: number, z1: number, y: number, style: string): void {
  const rng = ctx.rng;
  const w = x1 - x0, d = z1 - z0;
  if (w < 10 || d < 10) return;
  const n = 1 + Math.floor(rng() * 3);
  for (let i = 0; i < n; i++) {
    const px = randRange(rng, x0 + 3, x1 - 3), pz = randRange(rng, z0 + 3, z1 - 3);
    const r = rng();
    if (r < 0.35) {
      // stair / lift house
      ctx.solid.box(px, y, pz, 3 + rng() * 2, 2.6 + rng(), 2.6 + rng() * 2, style === 'modern' ? 0x3a4048 : 0xa8a49c);
    } else if (r < 0.55) {
      // water tank
      ctx.metal.cyl(px, y + 0.9, pz, 1.1, 1.1, 2.0, 0x8a8f96, 12);
      ctx.metal.cyl(px, y + 2.9, pz, 0.2, 1.1, 0.5, 0x7a7f86, 12);
      for (const [dx, dz] of [[-0.7, -0.7], [0.7, -0.7], [-0.7, 0.7], [0.7, 0.7]]) ctx.metal.box(px + dx, y, pz + dz, 0.12, 0.9, 0.12, 0x555a60);
    } else if (r < 0.8) {
      // antenna
      ctx.metal.cyl(px, y, pz, 0.05, 0.08, 4 + rng() * 4, 0x606468, 6);
      ctx.metal.box(px, y + 3, pz, 1.2, 0.05, 0.05, 0x606468);
    } else {
      // AC units
      ctx.solid.box(px, y, pz, 1.2, 0.8, 1.2, 0x9a9a9a);
      ctx.solid.box(px, y + 0.8, pz, 1.1, 0.06, 1.1, 0x555555);
    }
  }
  // billboard on some mid-rise roofs
  if (rng() < 0.12 && w > 14) {
    ctx.billboardRequests.push({ x: (x0 + x1) / 2, y: y + 3.5, z: rng() < 0.5 ? z1 - 1 : z0 + 1, ry: rng() < 0.5 ? 0 : Math.PI, w: Math.min(10, w - 2) });
  }
}

interface Lot {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
}

function pickStyle(ctx: BuildCtx, buda: boolean, tower: boolean): FacadeStyle {
  const F = ctx.tex.facades;
  const r = ctx.rng();
  if (tower) return F.modern;
  if (buda) return r < 0.45 ? F.classic : r < 0.7 ? F.terracotta : r < 0.85 ? F.brick : F.stone;
  return r < 0.34 ? F.classic : r < 0.52 ? F.terracotta : r < 0.66 ? F.modern : r < 0.82 ? F.brick : r < 0.94 ? F.panel : F.stone;
}

function streetSides(b: Block, lot: Lot): string {
  let s = '';
  if (Math.abs(lot.z0 - b.z0) < 2.5) s += 'N';
  if (Math.abs(lot.z1 - b.z1) < 2.5) s += 'S';
  if (Math.abs(lot.x0 - b.x0) < 2.5) s += 'W';
  if (Math.abs(lot.x1 - b.x1) < 2.5) s += 'E';
  return s;
}

export function buildBlocks(ctx: BuildCtx, parkSoup: QuadSoup): void {
  const L = ctx.layout;
  const rng = ctx.rng;
  for (const b of L.blocks) {
    if (b.landmark) continue;
    const buda = b.buda;
    const w = b.x1 - b.x0, d = b.z1 - b.z0;
    const r = rng();
    // near the river on the Pest side: grander stone buildings
    const riverside = !buda && b.c0 === L.riverCol + 1;
    if (!buda && r < 0.22) {
      // perimeter block with courtyard
      const st = riverside ? ctx.tex.facades.stone : pickStyle(ctx, buda, false);
      const floors = riverside ? 4 + Math.floor(rng() * 2) : 4 + Math.floor(rng() * 4);
      const shops = rng() < 0.8;
      const D = 13 + rng() * 3;
      const tint = _tint.set(pick(rng, st.tints)).clone();
      const o: BuildingOpts = { style: st, shops, floors, tint };
      addBuildingBox(ctx, b.x0, b.z0, b.x1, b.z0 + D, { ...o, streetSides: 'NEW' });
      addBuildingBox(ctx, b.x0, b.z1 - D, b.x1, b.z1, { ...o, streetSides: 'SEW' });
      addBuildingBox(ctx, b.x0, b.z0 + D, b.x0 + D, b.z1 - D, { ...o, streetSides: 'W', plain: true });
      addBuildingBox(ctx, b.x1 - D, b.z0 + D, b.x1, b.z1 - D, { ...o, streetSides: 'E', plain: true });
      // courtyard: trees + benches
      const cx = (b.x0 + b.x1) / 2, cz = (b.z0 + b.z1) / 2;
      for (let i = 0; i < 4; i++) ctx.treeRequests.push({ x: cx + (rng() - 0.5) * 14, z: cz + (rng() - 0.5) * 14, s: 0.8 + rng() * 0.4 });
      ctx.benchRequests.push({ x: cx, z: cz + 6, ry: 0 });
      continue;
    }
    // subdivide into lots
    const lots: Lot[] = [];
    const splitX = w > 34 && rng() < 0.85;
    const splitZ = d > 34 && rng() < 0.85;
    const sx = splitX ? b.x0 + w * randRange(rng, 0.4, 0.6) : b.x1;
    const sz = splitZ ? b.z0 + d * randRange(rng, 0.4, 0.6) : b.z1;
    const xs = splitX ? [b.x0, sx, b.x1] : [b.x0, b.x1];
    const zs = splitZ ? [b.z0, sz, b.z1] : [b.z0, b.z1];
    for (let i = 0; i + 1 < xs.length; i++) for (let j = 0; j + 1 < zs.length; j++) lots.push({ x0: xs[i], z0: zs[j], x1: xs[i + 1], z1: zs[j + 1] });
    // occasionally merge two lots into an L-shape / bigger building
    let parkDone = false;
    for (const lot of lots) {
      const lw = lot.x1 - lot.x0, ld = lot.z1 - lot.z0;
      const gap = 0.6 + rng() * 1.6;
      // shrink toward the block interior (keep street edges flush, minus a setback sometimes)
      const setback = rng() < 0.3 ? 1 + rng() * 1.5 : 0;
      const x0 = lot.x0 + (Math.abs(lot.x0 - b.x0) < 1 ? setback : gap);
      const x1 = lot.x1 - (Math.abs(lot.x1 - b.x1) < 1 ? setback : gap);
      const z0 = lot.z0 + (Math.abs(lot.z0 - b.z0) < 1 ? setback : gap);
      const z1 = lot.z1 - (Math.abs(lot.z1 - b.z1) < 1 ? setback : gap);
      const sides = streetSides(b, lot);
      if (!parkDone && lots.length > 2 && rng() < 0.16) {
        // small park
        parkDone = true;
        parkSoup.floor(lot.x0 + 0.5, lot.z0 + 0.5, lot.x1 - 0.5, lot.z1 - 0.5, 0.115, 8);
        const n = 4 + Math.floor(rng() * 5);
        for (let i = 0; i < n; i++) ctx.treeRequests.push({ x: randRange(rng, lot.x0 + 3, lot.x1 - 3), z: randRange(rng, lot.z0 + 3, lot.z1 - 3), s: 0.8 + rng() * 0.5 });
        ctx.benchRequests.push({ x: (lot.x0 + lot.x1) / 2, z: (lot.z0 + lot.z1) / 2, ry: rng() * Math.PI });
        continue;
      }
      const tower = !buda && lots.length >= 2 && !riverside && rng() < 0.07 && lw > 18 && ld > 18;
      const st = riverside ? (rng() < 0.6 ? ctx.tex.facades.stone : ctx.tex.facades.classic) : pickStyle(ctx, buda, tower);
      const shops = buda ? rng() < 0.35 : rng() < 0.75;
      let floors: number;
      if (tower) floors = 10 + Math.floor(rng() * 9);
      else if (buda) floors = 2 + Math.floor(rng() * 3);
      else if (st.key === 'modern') floors = 4 + Math.floor(rng() * 5);
      else if (st.key === 'panel') floors = 5 + Math.floor(rng() * 5);
      else floors = 3 + Math.floor(rng() * 5);
      const tint = _tint.set(pick(rng, st.tints)).clone();
      if (tower) {
        // podium + set-back tower
        const podium = 3;
        addBuildingBox(ctx, x0, z0, x1, z1, { style: st, shops, floors: podium, tint, plain: true, streetSides: sides });
        const ins = 3 + rng() * 2;
        const ph = (shops ? ctx.tex.shop.tileH : 0) + podium * st.floorH;
        const soup = facadeSoup(ctx, st.key);
        const uOff = Math.floor(rng() * st.bays) * st.bayW;
        const th = ph + floors * st.floorH;
        facadeBox(soup, st, x0 + ins, z0 + ins, x1 - ins, z1 - ins, ph, th, uOff, 0, tint);
        ctx.roofs.floor(x0 + ins, z0 + ins, x1 - ins, z1 - ins, th, 12);
        ctx.solid.box((x0 + x1) / 2, th, (z0 + z1) / 2, x1 - x0 - ins * 2 + 0.2, 1.0, z1 - z0 - ins * 2 + 0.2, CORNICE.modern);
        ctx.solid.box((x0 + x1) / 2, th + 1, (z0 + z1) / 2, 4, 2.5, 3, 0x3a4048);
        ctx.metal.cyl((x0 + x1) / 2, th + 3.5, (z0 + z1) / 2, 0.08, 0.15, 9, 0x8a8f96, 6);
        ctx.colliders.push(aabb(x0 + ins, z0 + ins, x1 - ins, z1 - ins, th));
        continue;
      }
      if (lw > 22 && ld > 22 && rng() < 0.35) {
        // L-shape: two boxes sharing a corner
        const cornerX = rng() < 0.5, cornerZ = rng() < 0.5;
        const aw = lw * randRange(rng, 0.5, 0.65), ad = ld * randRange(rng, 0.5, 0.65);
        const ax0 = cornerX ? x0 : x1 - lw, ax1 = ax0 + lw;
        const az0 = cornerZ ? z0 : z1 - ad, az1 = az0 + ad;
        const bx0 = cornerX ? x0 : x1 - aw, bx1 = bx0 + aw;
        const bz0 = cornerZ ? z0 : z1 - ld, bz1 = bz0 + ld;
        const o: BuildingOpts = { style: st, shops, floors, tint, streetSides: sides };
        addBuildingBox(ctx, ax0, az0, ax1, az1, o);
        addBuildingBox(ctx, bx0, bz0, bx1, bz1, { ...o, floors: Math.max(2, floors - (rng() < 0.5 ? 1 : 0)), plain: true });
        continue;
      }
      addBuildingBox(ctx, x0, z0, x1, z1, { style: st, shops, floors, tint, streetSides: sides });
    }
  }
}
